import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router, RouterModule } from '@angular/router';
import { LoadSpinnerWrapperComponent } from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import { CommonDirectives } from '@app/shared/directives/common-directives';
import { CustomValidators } from '@app/shared/services/customValidators';
import { UserAuthService } from '../../services/userAuth.service';
import { WebResponse } from '@app/shared/interfaces/webResponse.interface';
import { ToastService } from '@app/core/services/toast.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-force-change-password',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    LoadSpinnerWrapperComponent,
    RouterModule,
    CommonDirectives
  ],
  templateUrl: './force-change-password.component.html',
  styleUrl: './force-change-password.component.scss'
})
export class ForceChangePasswordComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  loading: boolean = false;
  userName: string = '';
  session: string = '';
  ngDestroy$ = new Subject<boolean>();
  
  constructor(
    private fb: FormBuilder,
    private customValidators: CustomValidators,
    private router: Router,
    private userAuthService: UserAuthService,
    private toastService: ToastService
  ) { 
    this.router.routerState.root.queryParams
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(params => {
        this.userName = params['userName'] || '';
        this.session = params['session'] || '';
      });
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
    this.ngDestroy$.complete();
  }

  ngOnInit(): void {
    this.form = this.fb.group(
      {
      password: [null, [Validators.required, this.customValidators.passwordPolicyValidator]],
      confirmPassword: [null, Validators.required]
    }, 
    { validators: this.customValidators.passwordMatchValidator('password', 'confirmPassword') });
  }

  setNewPassword() {
    if ( !this.userName || !this.session ) {
      this.toastService.showErrorToast("User information is missing. Unable to set new password.");
      return;
    }

    if (this.form.invalid) return;
    this.loading = true;
    const newPassword = this.form.get('password')?.value;
    this.userAuthService.forceChangePassword(this.userName, newPassword, this.session)
      .subscribe((response: WebResponse<boolean>) => {
        this.loading = false;
        if (response.succeeded) {
          this.toastService.showSuccessToast("Password changed successfully. Please log in with your new password.");
          this.form.reset();
          this.router.navigate(['/portal-login']);
        } else {
          this.toastService.showErrorToast("Failed to set new password. Please try again.");
        }
      });
  }
}
